import { buildMetric } from "../metrics";
import {
  COMPARE_METRIC_LOOKUP,
  CONTENT_METRICS,
  METRIC_COLUMNS_BY_ENTITY,
  METRIC_GROUPING_LOOKUP,
  PRICING_METRICS,
  PROMO_METRICS,
} from "./metricsByEntity";
import checkApplication from "utils/checkApplication";
import { CONTENT, DEALS, DYNAMIC_PRICING } from "components/custom/merchandise/constants";
import buildMetricColumn, { type MetricColumn } from "../columns/buildMetricColumn";
import { useAppSelector } from "../../../redux/hooks";
import { useMemo } from "react";

export const useStandardEntityMetrics = (entityType: string | null | undefined) => {
  const user = useAppSelector((state) => state.user);
  return useMemo(() => {
    if (!entityType) {
      return [];
    }
    let entityMetrics = METRIC_COLUMNS_BY_ENTITY[entityType] ?? [];

    if (!checkApplication(user, DYNAMIC_PRICING)) {
      entityMetrics = entityMetrics.filter((id) => !PRICING_METRICS.includes(id));
    }

    if (!checkApplication(user, CONTENT)) {
      entityMetrics = entityMetrics.filter((id) => !CONTENT_METRICS.includes(id));
    }

    if (!checkApplication(user, DEALS)) {
      entityMetrics = entityMetrics.filter((id) => !PROMO_METRICS.includes(id));
    }

    return entityMetrics;
  }, [entityType, user]);
};

export const extendAllEntityMetricOptions = (
  standardIds: string[],
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  data: any,
  specifiedColumns: Array<MetricColumn>,
  marketPlace: { marketPlace: string },
  format = "table",
) => {
  for (const metricId of standardIds) {
    if (!COMPARE_METRIC_LOOKUP[metricId] || specifiedColumns.find((col) => col.id === metricId)) {
      continue;
    }
    const extraMetricSpec = buildMetricColumn(marketPlace, metricId, COMPARE_METRIC_LOOKUP[metricId], {
      checked: false,
    });

    let extraOverviewMetricSpec = {};
    if (format === "overview") {
      extraOverviewMetricSpec = buildMetric(marketPlace, data, metricId, COMPARE_METRIC_LOOKUP[metricId]);
    }

    extraMetricSpec.grouping = METRIC_GROUPING_LOOKUP[metricId];

    // Below is to filter out duplicate metric names and descriptions that come up from legacy tables
    // For example, when cost and ad spend are used as keys, they have the same label, accessor, and tooltip, but different ids passed in
    if (!specifiedColumns.some((col) => col.Header === extraMetricSpec.Header)) {
      specifiedColumns.push({ ...extraMetricSpec, ...extraOverviewMetricSpec });
    }
  }

  specifiedColumns = specifiedColumns.map((metric) => ({
    ...metric,
    name: "name" in metric ? metric.name : (metric.Header as string),
    label: "label" in metric ? metric.label : (metric.Header as string),
  }));

  return specifiedColumns;
};

// Take legacy key/id and get global metric option
// Once we convert legacy ad and merch dashboard tables this can be removed
export const LEGACY_METRIC_IDS_MAP = {
  total_sales: "sales",
  total_cost: "cost",
  total_units: "units",
  total_roas: "troas",
  conv_rate: "conversion_rate",
  conversion: "orders",
  click_thru_rate: "ctr",
  cost_per_click: "cpc",
};
